/**
 * Vocabulaire d'affichage du poids — pur, sans React.
 *
 * Même rôle que `lib/macros.ts` pour les macros : nommer et mettre en forme, une
 * fois, pour que le graphique, l'historique et le volet objectif ne divergent
 * pas. Trois écrans qui formatent « 78,4 kg » chacun à sa façon finissent par
 * l'écrire de trois façons.
 *
 * ⚠️ Aucun calcul de poids ici : la moyenne lissée, la tendance et l'écart au
 * rythme visé sont établis par le serveur (§4.6). Ce fichier ne fait que traduire
 * des nombres en texte.
 */

/**
 * Bornes de saisie, **miroir de celles de `routes/weight.ts`**.
 *
 * ⚠️ Duplication assumée et volontairement minuscule : elle sert uniquement à
 * désactiver un bouton avant l'aller-retour. Le serveur reste seul juge et
 * refuserait la valeur de toute façon (400) — le client ne fait qu'éviter un
 * voyage pour rien, jamais autoriser ce que le serveur refuse.
 */
export const MIN_KG = 20;
export const MAX_KG = 400;
export const MAX_RATE_KG_WEEK = 3;

/**
 * « 78,4 » — toujours une décimale.
 *
 * ⚠️ Une décimale **fixe**, y compris pour un poids rond : sans
 * `minimumFractionDigits`, une liste alternerait « 78 » et « 78,4 » et les
 * colonnes de chiffres tabulaires ne s'aligneraient plus.
 */
export function formatKg(value: number): string {
  return value.toLocaleString('fr-FR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

/**
 * Valeur **signée** — « +0,25 » / « −0,25 » — pour les tendances et les écarts.
 *
 * ⚠️ Le signe moins est le vrai (U+2212), pas un trait d'union : c'est ce
 * qu'utilise déjà l'écart 4/4/9 de la Bibliothèque, et un tiret ASCII se lit mal
 * à côté d'un « + » dans la même colonne.
 *
 * ⚠️ Le « + » est explicite. Sans lui, « 0,25 kg/semaine » ne dirait pas si on
 * monte ou si on descend — c'est toute l'information d'une tendance de poids.
 */
export function formatSigned(value: number, digits = 2): string {
  const magnitude = Math.abs(value).toLocaleString('fr-FR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  if (value > 0) return `+${magnitude}`;
  if (value < 0) return `−${magnitude}`;
  return magnitude;
}

/** « 12 août » — sans année, la plage la donne déjà. */
export function formatDayShort(date: string): string {
  if (date === '') return '';
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));
}

/**
 * « Lun. 18 août » — le jour de la semaine aide à repérer un trou de week-end.
 *
 * ⚠️ Capitalisation de la **première lettre seulement**, comme `formatDayLabel`
 * de `lib/dates.ts` : `text-transform: capitalize` écrirait « Lun. 18 Août », et
 * en français les mois sont en minuscules.
 */
export function formatDayRow(date: string): string {
  const label = new Intl.DateTimeFormat('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));
  return label.charAt(0).toUpperCase() + label.slice(1);
}
